import config from '@/config';
import api from '@/services/utils/base';
import {
  getSupportWidgetCustomAttributes,
  getSupportWidgetIdentifier,
  getSupportWidgetName,
  SupportWidgetUser,
} from '@/services/utils/support-widget';

const CHATWOOT_BASE_URL = 'https://chatwoot.bemob.com';

type ChatwootIdentifiableUser = SupportWidgetUser;

type ChatwootWidgetSession = {
  cwConversationToken: string;
  websiteToken: string;
};

let loadPromise: Promise<void> | null = null;
let lastIdentifiedUserId: string | null = null;
let identifiedUserId: string | null = null;
let identifyPromise: Promise<void> | null = null;
let hasBootstrappedChatwootSession = false;

const IDENTIFY_SETTLE_MS = 1200;

const shouldLogChatwoot = () => window.localStorage.getItem('debug-chatwoot') === '1';

const logChatwoot = (event: string, payload: Record<string, unknown> = {}) => {
  if (!shouldLogChatwoot()) {
    return;
  }

  console.info('[chatwoot]', event, payload);
};

const logChatwootSync = (event: string, payload: Record<string, unknown> = {}) => {
  console.info('[chatwoot-sync]', event, payload);
};

const waitForIdentifySettle = () => new Promise<void>((resolve) => window.setTimeout(resolve, IDENTIFY_SETTLE_MS));

const buildCustomAttributes = (user: ChatwootIdentifiableUser) => getSupportWidgetCustomAttributes(user);

const setCustomAttributes = (user: ChatwootIdentifiableUser) => {
  if (!(window as any).$chatwoot?.setCustomAttributes) {
    return;
  }

  (window as any).$chatwoot.setCustomAttributes(buildCustomAttributes(user));
};

const getChatwootWidgetSession = (): ChatwootWidgetSession | null => {
  const widgetFrame = Array.from(document.querySelectorAll('iframe')).find((frame) =>
    typeof frame.src === 'string' && frame.src.startsWith(`${CHATWOOT_BASE_URL}/widget`)
  );

  if (!widgetFrame?.src) {
    return null;
  }

  const widgetUrl = new URL(widgetFrame.src);
  const websiteToken = widgetUrl.searchParams.get('website_token');
  const cwConversationToken = widgetUrl.searchParams.get('cw_conversation');

  if (!websiteToken || !cwConversationToken) {
    return null;
  }

  return {
    websiteToken,
    cwConversationToken,
  };
};

const syncWidgetContact = async (user: ChatwootIdentifiableUser) => {
  const session = getChatwootWidgetSession();

  if (!session) {
    logChatwootSync('widget-session-missing', {userId: user.id});
    return;
  }

  logChatwootSync('contact-patch-start', {
    userId: user.id,
    hasEmail: !!user.email,
    hasIdentifierHash: !!user.chatwootIdentifierHash,
  });

  const response = await api.post('/user/chatwoot-contact-sync', {
    websiteToken: session.websiteToken,
    cwConversationToken: session.cwConversationToken,
  });

  const payload = response?.data?.payload;
  logChatwootSync('contact-patch-done', {
    userId: user.id,
    hasEmail: !!payload?.email || !!payload?.has_email,
    identifier: payload?.identifier ?? null,
  });
};

const applySetUser = async (user: ChatwootIdentifiableUser) => {
  const identifier = getSupportWidgetIdentifier(user);
  const name = getSupportWidgetName(user);

  (window as any).$chatwoot.setUser(identifier, {
    name,
    email: user.email,
    identifier_hash: user.chatwootIdentifierHash,
  });

  setCustomAttributes(user);
  identifiedUserId = identifier;
  lastIdentifiedUserId = identifier;
  logChatwoot('setUser', {
    userId: user.id,
    hasIdentifierHash: !!user.chatwootIdentifierHash,
    hasEmail: !!user.email,
  });

  await waitForIdentifySettle();
  await syncWidgetContact(user);
  hasBootstrappedChatwootSession = true;
  logChatwoot('setUser-settled', {userId: user.id});
};

const whenChatwootReady = (run: () => Promise<void> | void): Promise<void> => {
  if ((window as any).$chatwoot?.setUser) {
    return Promise.resolve(run());
  }

  return new Promise((resolve, reject) => {
    window.addEventListener('chatwoot:ready', () => {
      logChatwoot('ready');
      Promise.resolve(run()).then(resolve).catch(reject);
    }, {once: true});
  });
};

const resetAndSetUser = (user: ChatwootIdentifiableUser): Promise<void> => {
  return new Promise((resolve, reject) => {
    window.addEventListener('chatwoot:ready', () => {
      logChatwoot('ready-after-reset');
      void applySetUser(user).then(resolve).catch(reject);
    }, {once: true});
    logChatwoot('reset');
    (window as any).$chatwoot.reset();
  });
};

const initChatwootSdk = (onReady?: () => Promise<void> | void): Promise<void> => {
  if (loadPromise) {
    return onReady ? loadPromise.then(() => whenChatwootReady(onReady)) : loadPromise;
  }

  const websiteToken = config.chatwootWebsiteToken;

  if (!websiteToken) {
    return Promise.reject(new Error('chatwootWebsiteToken is not configured'));
  }

  loadPromise = new Promise((resolve, reject) => {
    (window as any).chatwootSettings = {
      hideMessageBubble: true,
      // The widget's own copy reads "We are away at the moment" whenever no
      // human agent is online. That is true of the humans and wrong about the
      // bot, which answers around the clock, so both states say the same thing.
      availableMessage: 'Have a question about BeMob? Send us a message.',
      unavailableMessage: 'Have a question about BeMob? Send us a message.'
    };

    const script = document.createElement('script');
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.dataset.chatwootSdk = 'true';

    script.onload = () => {
      logChatwoot('sdk-loaded', {baseUrl: CHATWOOT_BASE_URL});

      window.addEventListener('chatwoot:ready', () => {
        logChatwoot('ready');
        Promise.resolve(onReady?.()).then(resolve).catch(reject);
      }, {once: true});

      window.addEventListener('chatwoot:error', (event) => {
        logChatwoot('error', {event});
      }, {once: true});

      logChatwoot('sdk-run', {websiteToken});
      (window as any).chatwootSDK.run({
        websiteToken,
        baseUrl: CHATWOOT_BASE_URL
      });
    };

    script.onerror = () => {
      logChatwoot('sdk-load-failed');
      loadPromise = null;
      reject(new Error('Failed to load Chatwoot SDK'));
    };

    document.body.appendChild(script);
  });

  return loadPromise;
};

/** Authenticated panel — load widget and identify user on first ready. */
export const loadChatwootSdk = (user: ChatwootIdentifiableUser): Promise<void> => {
  if (!user?.id) {
    return Promise.resolve();
  }

  if ((window as any).$chatwoot?.setUser) {
    return identifyExistingWidget(user);
  }

  return initChatwootSdk(() => applySetUser(user));
};

const identifyExistingWidget = (user: ChatwootIdentifiableUser): Promise<void> => {
  if (!hasBootstrappedChatwootSession) {
    logChatwootSync('force-reset-initial-sync', {userId: user.id});
    return resetAndSetUser(user);
  }

  if (lastIdentifiedUserId !== null && lastIdentifiedUserId !== user.id) {
    return resetAndSetUser(user);
  }

  return whenChatwootReady(() => applySetUser(user));
};

export const ensureChatwootUserIdentified = (
  user: ChatwootIdentifiableUser | null | undefined
): Promise<void> => {
  if (!user?.id) {
    return Promise.resolve();
  }

  if (identifyPromise) {
    logChatwootSync('reuse-pending-sync', {userId: user.id});
    return identifyPromise;
  }

  logChatwootSync('start', {
    userId: user.id,
    hasEmail: !!user.email,
    hasIdentifierHash: !!user.chatwootIdentifierHash,
    widgetReady: !!(window as any).$chatwoot?.setUser,
  });

  identifyPromise = (async () => {
    if ((window as any).$chatwoot?.setUser) {
      await identifyExistingWidget(user);
    } else {
      await loadChatwootSdk(user);
    }
    logChatwootSync('done', {userId: user.id});
  })().catch((error) => {
    logChatwootSync('failed', {
      userId: user.id,
      message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }).finally(() => {
    identifyPromise = null;
  });

  return identifyPromise;
};

export const logoutChatwootSession = () => {
  lastIdentifiedUserId = null;
  identifiedUserId = null;
  identifyPromise = null;
  hasBootstrappedChatwootSession = false;
  logChatwoot('logout-reset');
  (window as any).$chatwoot?.reset?.();
};

export const syncChatwootUser = (user: ChatwootIdentifiableUser | null | undefined) => {
  if (!user?.id) {
    return Promise.resolve();
  }

  return ensureChatwootUserIdentified(user).catch((error) => {
    logChatwootSync('background-sync-failed', {
      userId: user.id,
      message: error instanceof Error ? error.message : String(error),
    });
  });
};

export const isChatwootUserSynchronized = (
  user: ChatwootIdentifiableUser | null | undefined
) => !!user?.id && identifiedUserId === user.id && !identifyPromise && hasBootstrappedChatwootSession;
